// components/tools/Tools.jsx
import React, { useMemo, useState } from "react";
import "./Tools.css";

/* ---------- helpers ---------- */
const trim = (s) => s.trim();
const isHex = (s) => /^[a-f0-9]+$/i.test(s);
const isMd5 = (s) => s.length === 32 && isHex(s);
const isSha1 = (s) => s.length === 40 && isHex(s);
const isSha256 = (s) => s.length === 64 && isHex(s);
const isIpv4 = (s) =>
  /^(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)){3}$/.test(s);
const isDomain = (s) =>
  /^([a-z0-9-]{1,63}\.)+[a-z]{2,}$/i.test(s) && !isIpv4(s);

/* ---------- IOC Inspector ---------- */
function iocType(value) {
  const v = trim(value);
  if (!v) return null;
  if (isMd5(v)) return "md5";
  if (isSha1(v)) return "sha1";
  if (isSha256(v)) return "sha256";
  if (isIpv4(v)) return "ip";
  if (isDomain(v)) return "domain";
  try {
    const u = new URL(v);
    if (u.protocol === "http:" || u.protocol === "https:") return "url";
  } catch {}
  return "unknown";
}
function vtLink(value, type) {
  switch (type) {
    case "ip": return `https://www.virustotal.com/gui/ip-address/${value}`;
    case "domain": return `https://www.virustotal.com/gui/domain/${value}`;
    case "url": return `https://www.virustotal.com/gui/url/${encodeURIComponent(value)}`;
    case "md5":
    case "sha1":
    case "sha256": return `https://www.virustotal.com/gui/file/${value}`;
    default: return "https://www.virustotal.com";
  }
}
function outLinks(value, type) {
  const links = [];
  if (type === "ip") {
    links.push(["VirusTotal", vtLink(value, type)]);
    links.push(["AbuseIPDB", `https://abuseipdb.com/check/${value}`]);
    links.push(["Shodan", `https://www.shodan.io/host/${value}`]);
    links.push(["Talos", `https://talosintelligence.com/reputation_center/lookup?search=${value}`]);
  } else if (type === "domain") {
    links.push(["VirusTotal", vtLink(value, type)]);
    links.push(["urlscan", `https://urlscan.io/domain/${value}`]);
    links.push(["SecurityTrails", `https://securitytrails.com/domain/${value}`]);
    links.push(["DNSViz", `https://dnsviz.net/d/${value}/dnssec/`]);
  } else if (type === "url") {
    links.push(["VirusTotal", vtLink(value, type)]);
    links.push(["urlscan", `https://urlscan.io/search/#${encodeURIComponent(value)}`]);
    links.push(["Unshorten", `https://unshorten.it/${encodeURIComponent(value)}`]);
  } else if (type === "md5" || type === "sha1" || type === "sha256") {
    links.push(["VirusTotal", vtLink(value, type)]);
    links.push(["Hybrid Analysis", `https://www.hybrid-analysis.com/search?query=${value}`]);
    links.push(["Malshare", `https://malshare.com/search.php?query=${value}`]);
  }
  return links;
}

/* ---------- DNS Resolver ---------- */
async function fetchDns(name, type) {
  const url = `https://dns.google/resolve?name=${encodeURIComponent(name)}&type=${encodeURIComponent(type)}`;
  const res = await fetch(url);
  return res.json();
}

/* ---------- CVE Inspector (CIRCL -> NVD fallback) ---------- */
const CVE_ID_RE = /^CVE-\d{4}-\d{4,}$/i;

function cveLinks(cve) {
  if (!cve) return [];
  return [
    ["NVD", `https://nvd.nist.gov/vuln/detail/${cve}`],
    ["MITRE", `https://cve.mitre.org/cgi-bin/cvename.cgi?name=${cve}`],
    ["CISA KEV", `https://www.cisa.gov/known-exploited-vulnerabilities-catalog?search_api_fulltext=${cve}`],
    ["ExploitDB", `https://www.exploit-db.com/search?cve=${cve}`],
  ];
}

async function fetchCveCIRCL(cveId) {
  const url = `https://cve.circl.lu/api/cve/${encodeURIComponent(cveId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("CIRCL failed");
  return res.json();
}
async function fetchCveNVD(cveId) {
  const url = `https://services.nvd.nist.gov/rest/json/cves/2.0?cveId=${encodeURIComponent(cveId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error("NVD failed");
  return res.json();
}

// Normalize CIRCL and NVD responses to a common shape
function normalizeCIRCL(d) {
  if (!d) return null;
  const refs = Array.isArray(d.references)
    ? d.references.map((r) => ({ url: r?.url || r, name: r?.name || "", source: r?.source || "" }))
    : [];
  return {
    id: d.id || d.CVE,
    summary: d.summary || d.description || "",
    published: d.Published || d.published || "",
    modified: d.Modified || d.lastModified || "",
    cwe: d.cwe || "",
    cvss3: d.cvss?.baseScore ?? d.cvss3?.baseScore ?? d.cvssV3?.baseScore ?? null,
    vector3: d.cvss?.vector ?? d.cvss3?.vectorString ?? "",
    cvss2: d.cvss?.score ?? d.cvss2?.baseScore ?? null,
    references: refs,
  };
}
function normalizeNVD(d) {
  try {
    const v = d?.vulnerabilities?.[0]?.cve;
    if (!v) return null;
    const id = v.id;
    const desc = v.descriptions?.[0]?.value || "";
    const published = v.published || "";
    const modified = v.lastModified || "";
    const weaknesses = v.weaknesses?.[0]?.description?.[0]?.value || "";
    const refs = (v.references || []).map((r) => ({
      url: r?.url,
      name: r?.source || new URL(r?.url).hostname,
      source: r?.source || "",
    }));

    // CVSS v3.x
    const m31 = v?.metrics?.cvssMetricV31?.[0]?.cvssData;
    const m30 = v?.metrics?.cvssMetricV30?.[0]?.cvssData;
    const cvss3Data = m31 || m30 || null;
    const cvss3 = cvss3Data?.baseScore ?? null;
    const vector3 = cvss3Data?.vectorString || "";

    // CVSS v2
    const m2 = v?.metrics?.cvssMetricV2?.[0]?.cvssData;
    const cvss2 = m2?.baseScore ?? null;

    return { id, summary: desc, published, modified, cwe: weaknesses, cvss3, vector3, cvss2, references: refs };
  } catch {
    return null;
  }
}

async function lookupCVE(cveId) {
  try {
    const circl = await fetchCveCIRCL(cveId);
    const n = normalizeCIRCL(circl);
    if (n?.id) return n;
  } catch {}
  const nvd = await fetchCveNVD(cveId);
  const n2 = normalizeNVD(nvd);
  if (n2?.id) return n2;
  throw new Error("not found");
}

/* ---------- UI blocks ---------- */
function Section({ title, children, right }) {
  return (
    <div className="card mb-3 shadow-sm tools-card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <span className="fw-semibold">{title}</span>
        {right || null}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

/* ---------- main ---------- */
export default function Tools() {
  /* 1) IOC Inspector */
  const [ioc, setIoc] = useState("");
  const itype = useMemo(() => iocType(ioc), [ioc]);
  const links = useMemo(() => outLinks(ioc, itype), [ioc, itype]);

  /* 2) DNS Resolver */
  const [fqdn, setFqdn] = useState("");
  const [rtype, setRtype] = useState("A");
  const [dnsLoading, setDnsLoading] = useState(false);
  const [dnsAnswer, setDnsAnswer] = useState(null);
  async function resolveDns() {
    setDnsAnswer(null);
    if (!isDomain(fqdn)) {
      setDnsAnswer({ error: "Invalid domain name" });
      return;
    }
    setDnsLoading(true);
    try {
      const json = await fetchDns(fqdn, rtype);
      setDnsAnswer(json);
    } catch {
      setDnsAnswer({ error: "Resolution failed" });
    } finally {
      setDnsLoading(false);
    }
  }

  /* 3) CVE Inspector with fallback */
  const [cveId, setCveId] = useState("");
  const [cveLoading, setCveLoading] = useState(false);
  const [cveErr, setCveErr] = useState("");
  const [cve, setCve] = useState(null);

  async function loadCve() {
    const id = cveId.trim().toUpperCase();
    setCveErr("");
    setCve(null);
    if (!CVE_ID_RE.test(id)) {
      setCveErr("Invalid CVE ID. Example: CVE-2024-12345");
      return;
    }
    setCveLoading(true);
    try {
      const data = await lookupCVE(id);
      setCve(data);
    } catch (e) {
      setCveErr("Lookup failed on CIRCL and NVD. Open external sources below.");
    } finally {
      setCveLoading(false);
    }
  }

  return (
    <div className="tools-wrapper my-4">
      <div className="tools-bar">
        <h2 className="tools-title mb-0">Cyber Essentials</h2>
      </div>

      {/* 1) IOC Inspector */}
      <Section
        title="IOC Inspector"
        right={itype ? <span className="badge-soft-secondary text-uppercase">{itype}</span> : null}
      >
        <div className="row g-3">
          <div className="col-lg-8">
            <label className="form-label">Paste IP, domain, URL, or hash</label>
            <input
              className="form-control"
              value={ioc}
              onChange={(e) => setIoc(e.target.value)}
              placeholder="8.8.8.8 | example.com | https://... | d41d8cd98f00b204e9800998ecf8427e"
            />
            <div className="tools-help mt-1">Automatic type detection. No data leaves your browser.</div>
          </div>
          <div className="col-lg-4">
            <label className="form-label">Quick facts</label>
            <div className="tools-table" style={{ borderRadius: 6 }}>
              <div className="p-2">
                <div className="d-flex justify-content-between">
                  <span className="tools-muted">Type</span>
                  <strong>{itype || "n/a"}</strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="tools-muted">Length</span>
                  <strong>{trim(ioc).length}</strong>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12">
            <label className="form-label">Quick open</label>
            {itype && links.length > 0 ? (
              <div className="d-flex flex-wrap gap-2">
                {links.map(([label, href]) => (
                  <a key={label} className="btn btn-sm btn-outline-secondary" href={href} target="_blank" rel="noreferrer">
                    {label}
                  </a>
                ))}
              </div>
            ) : (
              <div className="tools-muted">No links available</div>
            )}
          </div>
        </div>
      </Section>

      {/* 2) DNS Resolver */}
      <Section title="DNS Resolver">
        <div className="row g-3">
          <div className="col-lg-6">
            <label className="form-label">Domain</label>
            <input className="form-control" value={fqdn} onChange={(e) => setFqdn(e.target.value)} placeholder="example.com" />
          </div>
          <div className="col-lg-2">
            <label className="form-label">Record type</label>
            <select className="form-select" value={rtype} onChange={(e) => setRtype(e.target.value)}>
              <option>A</option><option>AAAA</option><option>MX</option><option>TXT</option>
              <option>CNAME</option><option>NS</option><option>SOA</option>
            </select>
          </div>
          {/* Centered button, consistent alignment */}
          <div className="col-lg-4 d-flex align-items-center">
            <button className="btn btn-primary w-100" onClick={resolveDns} disabled={dnsLoading}>
              {dnsLoading ? "Resolving..." : "Resolve"}
            </button>
          </div>

          <div className="col-12">
            <label className="form-label">Answer</label>
            <pre className="tools-json p-2" style={{ maxHeight: 260, overflow: "auto" }}>
{dnsAnswer ? JSON.stringify(dnsAnswer, null, 2) : "—"}
            </pre>
            {isDomain(fqdn) && (
              <div className="mt-2 d-flex flex-wrap gap-2">
                <a className="btn btn-sm btn-outline-secondary" href={`https://dnsviz.net/d/${fqdn}/dnssec/`} target="_blank" rel="noreferrer">DNSViz</a>
                <a className="btn btn-sm btn-outline-secondary" href={`https://securitytrails.com/domain/${fqdn}`} target="_blank" rel="noreferrer">SecurityTrails</a>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* 3) CVE Inspector (CIRCL -> NVD) */}
      <Section title="CVE Inspector" right={cve?.cvss3 != null ? <span className="badge-soft-secondary">CVSS {cve.cvss3}</span> : null}>
        <div className="row g-3">
          <div className="col-lg-8">
            <label className="form-label">CVE ID</label>
            <input
              className="form-control"
              value={cveId}
              onChange={(e) => setCveId(e.target.value)}
              placeholder="CVE-2024-12345"
            />
            <div className="tools-help mt-1">Format: CVE-YYYY-NNNN</div>
          </div>
          {/* Fixed alignment for the button */}
          <div className="col-lg-4 d-flex align-items-center">
            <button className="btn btn-primary w-100" onClick={loadCve} disabled={cveLoading}>
              {cveLoading ? "Looking up..." : "Lookup"}
            </button>
          </div>

          <div className="col-12">
            {cveErr && <div className="tools-muted">{cveErr}</div>}
            {!cveErr && !cve && <div className="tools-muted">—</div>}
            {cve && (
              <>
                <div className="tools-table" style={{ borderRadius: 6 }}>
                  <div className="p-2">
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">CVE</span>
                      <strong>{cve.id}</strong>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">Published</span>
                      <code>{cve.published || "n/a"}</code>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">Last modified</span>
                      <code>{cve.modified || "n/a"}</code>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">CWE</span>
                      <code>{cve.cwe || "n/a"}</code>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">CVSS v3.x</span>
                      <code>{cve.cvss3 ?? "n/a"} {cve.vector3 ? `(${cve.vector3})` : ""}</code>
                    </div>
                    <div className="d-flex justify-content-between">
                      <span className="tools-muted">CVSS v2</span>
                      <code>{cve.cvss2 ?? "n/a"}</code>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <label className="form-label">Summary</label>
                  <pre className="tools-json p-2" style={{ whiteSpace: "pre-wrap" }}>
{cve.summary || "No summary available"}
                  </pre>
                </div>

                <div className="mt-3">
                  <label className="form-label">References</label>
                  {Array.isArray(cve.references) && cve.references.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      {cve.references.slice(0, 12).map((r, idx) => {
                        const href = r?.url;
                        let label =
                          r?.name ||
                          r?.source ||
                          (href
                            ? (() => {
                                try { return new URL(href).hostname; } catch { return `ref ${idx + 1}`; }
                              })()
                            : `ref ${idx + 1}`);
                        return (
                          <a key={idx} className="btn btn-sm btn-outline-secondary" href={href} target="_blank" rel="noreferrer">
                            {label}
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="tools-muted">No references listed</div>
                  )}
                </div>

                <div className="mt-3">
                  <label className="form-label">Open externally</label>
                  <div className="d-flex flex-wrap gap-2">
                    {cveLinks(cve.id).map(([lbl, href]) => (
                      <a key={lbl} className="btn btn-sm btn-outline-secondary" href={href} target="_blank" rel="noreferrer">
                        {lbl}
                      </a>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </Section>
    </div>
  );
}
